import { Injectable, OnDestroy } from '@angular/core';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { trace, ROOT_CONTEXT, Span, SpanStatusCode } from '@opentelemetry/api';
import { getCurrentNavigationSpan, setCurrentNavigationSpan } from '../../telemetry/otel-state';
import { stripQueryString } from '../../telemetry/telemetry.util';
import { SecurityService } from '@core/secutiry/security.service';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';

/**
 * Opens a "Page" span on every NavigationStart and ends it on the matching NavigationEnd /
 * NavigationCancel / NavigationError. The span's duration reflects how long the route change
 * took.
 *
 * Span flushing is left to the `BatchSpanProcessor` configured in `initOtel`; a one-time
 * `pagehide` listener there covers tab close and bfcache transitions so trailing spans aren't
 * lost.
 */
@Injectable({ providedIn: 'root' })
export class OtelNavigationService implements OnDestroy {
  private subscription?: Subscription;
  // Resolved authenticated principal, attached to navigation spans as `enduser.id`. Populated
  // asynchronously once the auth context resolves; null until then (and for unauthenticated users).
  private enduserId: string | null = null;

  constructor(router: Router, security: SecurityService) {
    // The service is constructed eagerly at startup (see AppModule). When OTel is disabled the
    // tracer is a no-op, so subscribing to router events and fetching the auth context would just
    // burn a network request and allocate throwaway spans on every navigation — bail out instead.
    if (!environment.otelEnabled) {
      return;
    }

    // Resolve the current user once at startup and attach it to navigation spans for correlation.
    // Best-effort: if the user is unauthenticated or the call fails, spans simply carry no user id.
    security
      .getAuthenticationContext()
      .then((ctx) => {
        this.enduserId = ctx?.principleName ?? null;
        // If a navigation span is already open by the time auth resolves, backfill it.
        const open = getCurrentNavigationSpan();
        if (open && this.enduserId) {
          open.setAttribute('enduser.id', this.enduserId);
        }
      })
      .catch(() => {
        // Unauthenticated or auth-context unavailable; leave enduserId null.
      });

    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Defensive: if a previous nav span is still open (shouldn't happen under normal flow,
        // but possible if the router emits NavigationStart back-to-back without a terminal
        // event — happens on aborted nav in some Angular versions), close it before opening
        // the new one.
        const prev = getCurrentNavigationSpan();
        if (prev) {
          prev.end();
        }
        const path = stripQueryString(event.url);
        const span = trace
          .getTracer(environment.datadog.service)
          .startSpan(`Page: ${path}`, {}, ROOT_CONTEXT);
        if (this.enduserId) {
          span.setAttribute('enduser.id', this.enduserId);
        }
        setCurrentNavigationSpan(span);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        const span = getCurrentNavigationSpan();
        if (span) {
          // Replace the raw URL path captured at NavigationStart with the parameterized route
          // pattern (e.g. `Page: /users/:id/edit` instead of `Page: /users/123/edit`) to avoid
          // span-name cardinality explosion in the tracing backend. On NavigationEnd the matched
          // route config is reliable. On NavigationCancel/NavigationError the navigation did not
          // complete, so the pattern is often unavailable — in that case bucket the span under a
          // bounded name and keep the raw path as an attribute rather than leaking it into the
          // (unbounded) span name.
          const pattern = buildRoutePattern(router);
          if (pattern) {
            span.updateName(`Page: ${pattern}`);
          } else if (!(event instanceof NavigationEnd)) {
            span.setAttribute('navigation.url', stripQueryString(event.url));
            span.updateName(
              event instanceof NavigationError ? 'Page: (error)' : 'Page: (cancelled)'
            );
          }
          if (event instanceof NavigationError) {
            // `event.error` is typed `any` and can be null/undefined, a string, or a plain object.
            // recordException only produces useful message/type/stack attributes for an Error, so
            // normalize non-Error values into one before recording.
            if (event.error) {
              const exception =
                event.error instanceof Error ? event.error : new Error(String(event.error));
              span.recordException(exception);
            }
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: event.error ? String(event.error) : 'Navigation error',
            });
          } else if (event instanceof NavigationCancel) {
            span.setAttribute('navigation.cancelled', true);
          }
          span.end();
        }
        // Keep the just-ended span as the "current" one for one microtask so straggler XHRs
        // fired during the navigation still parent correctly. Then clear it.
        queueMicrotask(() => {
          if (getCurrentNavigationSpan() === span) {
            setCurrentNavigationSpan(null);
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    const lastSpan: Span | null = getCurrentNavigationSpan();
    if (lastSpan) {
      lastSpan.end();
    }
    setCurrentNavigationSpan(null);
    this.subscription?.unsubscribe();
  }
}

/**
 * Walks the activated route snapshot tree and reconstructs the parameterized route pattern
 * (e.g. `/users/:id/edit`) from each matched route's `routeConfig.path`. Returns an empty
 * string if the router state is unavailable, in which case the caller keeps the raw path.
 */
function buildRoutePattern(router: Router): string {
  const segments: string[] = [];
  let route: ActivatedRouteSnapshot | null = router.routerState?.snapshot?.root ?? null;
  while (route) {
    const path = route.routeConfig?.path;
    if (path) {
      segments.push(path);
    }
    route = route.firstChild ?? null;
  }
  return segments.length ? `/${segments.join('/')}` : '';
}
